import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import axios from 'axios'
import { ExtendedPromise } from '../../utils/promises-utils'
import { useForm } from 'react-hook-form'
import { type AlertColor, Card, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import { formApiGatewayApiUrl } from '../../utils/api/servers'
import { useFirmwidePricingConfig } from '../../hooks/data/use-firmwide-pricing-config'
import { usePortfolios } from '../../hooks/data/use-portfolios'
import { formAlertErrorMessage, handleRaceSettleAllResponse } from '../../utils/string-utils'
import ControlledTextField from '../../components/inputs/textfield/controlled-text-field'
import StackColumnLayout from '../../layouts/stack-column-layout'
import BalancePlusDataGrid from './balance-plus-data-grid'
import ToastWithMessage from '../../components/toast/toast-with-message'

const MultiBalancePlusDataGrid: React.FC = () => {
  const { isAuthorized, accessToken } = useContext(AuthContext)

  const { firmwidePricingConfig: pricingConfig, mutate } = useFirmwidePricingConfig(isAuthorized, accessToken)
  const portfolios = usePortfolios(isAuthorized, accessToken)

  useEffect(() => {
    // defaultValues are cached on the first render, so we must reset on state change
    reset(pricingConfig, { keepDirty: false, keepDirtyValues: false })
  }, [pricingConfig])
  const {
    handleSubmit,
    reset,
    control,
    formState,
  } = useForm<Record<string, string>>({
    defaultValues: pricingConfig
  })
  const handleUpdate = useCallback((update: Record<string, string>) => {
    const changedFields = formState.dirtyFields
    const promises = Object.keys(changedFields)
      .map(async changedField => {
        const message = {
          timestamp: new Date().toISOString(),
          configName: changedField,
          configValue: update[changedField],
        }

        console.debug(`[Balance+] Queuing update request for the field: ${changedField}`)
        console.debug(message)

        return await axios.post(
          formApiGatewayApiUrl('strategy-management/firmwide-pricing-config'),
          message,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        )
      })

    ExtendedPromise
      .raceSettleAll(promises, 10000, new Error('timeout'))
      .then(response => {
        console.debug(response)
        handleRaceSettleAllResponse(response, false, displayToastWithMessage)
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
      .finally(() => {
        void mutate() // Pull latest data
        reset() // Reset form cache
      })
  }, [formState, accessToken])

  const dataGrids = useMemo<React.ReactNode[]>(() => {
    return Object.keys(portfolios)
      .map(portfolio => {
        return (<Grid item xs={12} key={portfolio}><BalancePlusDataGrid portfolioId={portfolio}/></Grid>)
      })
  }, [portfolios])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [])

  return (
    <StackColumnLayout>
      <Grid container spacing={4} padding={4} marginTop={0}>
        <Grid item xs={12}>
          <Typography color='secondary' gutterBottom>
            Firm-wide Pricing Config
          </Typography>
          <Card variant='outlined'>
            <CardContent>
              <form
                id={'balance-plus-form'}
                /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
                onSubmit={handleSubmit(handleUpdate)}
                noValidate
              >
                <Grid container padding={1} spacing={1} alignItems='baseline'>
                  <Grid item xs md='auto'>
                    <ControlledTextField
                      control={control}
                      name='firm_spread_bid_bps'
                      fieldId='firm_spread_bid_bps'
                      label='Global Spread Bid (bps)'
                      value={pricingConfig.firm_spread_bid_bps}
                    />
                  </Grid>
                  <Grid item xs md='auto'>
                    <ControlledTextField
                      control={control}
                      name='firm_spread_ask_bps'
                      fieldId='firm_spread_ask_bps'
                      label='Global Spread Ask (bps)'
                      value={pricingConfig.firm_spread_ask_bps}
                    />
                  </Grid>
                  <Grid item xs={12} md='auto'>
                    <Tooltip title='Update firmwide config'>
                      <span>
                        <IconButton
                          size='small'
                          form={'balance-plus-form'}
                          type='submit'
                        >
                          <SaveIcon/>
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid container padding={1} spacing={1} alignItems='baseline'>
                  <Grid item xs md='auto'>
                    <ControlledTextField
                        control={control}
                        name='firm_position_limit_bid_factor'
                        fieldId='firm_position_limit_bid_factor'
                        label='Position Limit (Bid)'
                        value={pricingConfig.firm_position_limit_bid_factor}
                    />
                  </Grid>
                  <Grid item xs md='auto'>
                    <ControlledTextField
                        control={control}
                        name='firm_position_limit_ask_factor'
                        fieldId='firm_position_limit_ask_factor'
                        label='Position Limit (Ask)'
                        value={pricingConfig.firm_position_limit_ask_factor}
                    />
                  </Grid>
                  <Grid item xs={4} />
                </Grid>
                <Grid container padding={1} spacing={1} alignItems='baseline'>
                  <Grid item xs md='auto'>
                    <ControlledTextField
                        control={control}
                        name='firm_group_long_risk_factor'
                        fieldId='firm_group_long_risk_factor'
                        label='Group Risk Factor (Long)'
                        value={pricingConfig.firm_group_long_risk_factor}
                    />
                  </Grid>
                  <Grid item xs md='auto'>
                    <ControlledTextField
                        control={control}
                        name='firm_group_short_risk_factor'
                        fieldId='firm_group_short_risk_factor'
                        label='Group Risk Factor (Short)'
                        value={pricingConfig.firm_group_short_risk_factor}
                    />
                  </Grid>
                  <Grid item xs md='auto'>
                    <ControlledTextField
                        control={control}
                        name='firm_group_gross_risk_factor'
                        fieldId='firm_group_gross_risk_factor'
                        label='Group Risk Factor (Gross)'
                        value={pricingConfig.firm_group_gross_risk_factor}
                    />
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {dataGrids}
      </Grid>

      <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
    </StackColumnLayout>
  )
}

export default MultiBalancePlusDataGrid
